import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const MusicPlayerContext = createContext(null);

function fisherYates(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// 트랙이 재생 가능한지 판별 (fileUrl 또는 youtubeVideoId)
function isPlayable(track) {
  return !!(track?.fileUrl || track?.youtubeVideoId);
}

export function MusicPlayerProvider({ children }) {
  const [playlist, setPlaylist] = useState(() => {
    try {
      const saved = localStorage.getItem('impink_playlist');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [currentIndex, setCurrentIndex] = useState(() => {
    try {
      const saved = localStorage.getItem('impink_playerIndex');
      if (saved === null || saved === '') return null;
      const idx = parseInt(saved, 10);
      return isNaN(idx) ? null : idx;
    } catch { return null; }
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [repeatMode, setRepeatMode] = useState('none'); // 'none' | 'all' | 'one'
  const [shuffleOrder, setShuffleOrder] = useState([]);

  const [showSongList, setShowSongList] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [lyricsBarVisible, setLyricsBarVisible] = useState(false);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const [songListItems, setSongListItems] = useState([]);
  const [songListPage, setSongListPage] = useState(1);
  const [songListTotal, setSongListTotal] = useState(0);
  const [songListTotalPages, setSongListTotalPages] = useState(0);
  const [songListLoading, setSongListLoading] = useState(false);
  const [songListSearch, setSongListSearch] = useState('');

  const audioRef = useRef(new Audio());
  const isDraggingRef = useRef(false);
  const stateRef = useRef({});
  const pendingPlayRef = useRef(null);

  // YouTube IFrame API refs
  const ytPlayerRef = useRef(null);
  const ytReadyRef = useRef(false);
  const ytTimerRef = useRef(null);
  const activeBackendRef = useRef('audio'); // 'audio' | 'youtube'

  // stateRef를 항상 최신 상태로 유지
  stateRef.current = { playlist, currentIndex, shuffleMode, shuffleOrder, repeatMode };

  // 플레이리스트 localStorage 저장
  useEffect(() => {
    try { localStorage.setItem('impink_playlist', JSON.stringify(playlist)); } catch {}
  }, [playlist]);

  useEffect(() => {
    try {
      localStorage.setItem('impink_playerIndex', currentIndex !== null ? String(currentIndex) : '');
    } catch {}
  }, [currentIndex]);

  // 현재 곡의 가사 자동 로드
  const _curSong = currentIndex !== null ? playlist[currentIndex] : null;
  const _curSongId = _curSong?.id ?? null;
  // 로드 완료 조건: 실제 가사 있음 / 이미 확인 완료(_lyricsChecked) / SongListPanel 플래그로 가사 없음 확정
  const _curLyricsLoaded = _curSong ? (
    !!_curSong.syncLyrics ||
    !!_curSong.lyrics ||
    _curSong._lyricsChecked === true ||
    (_curSong.hasLyrics === 0 && _curSong.hasSyncLyrics === 0)
  ) : true;

  useEffect(() => {
    if (!_curSongId || _curLyricsLoaded) return;
    let cancelled = false;
    axios.get(`/public/songs/${_curSongId}/lyrics`)
      .then(res => {
        if (cancelled) return;
        setPlaylist(prev => prev.map(t =>
          t.id === _curSongId
            ? { ...t, _lyricsChecked: true, lyrics: res.data.lyrics ?? null, syncLyrics: res.data.syncLyrics ?? null }
            : t
        ));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_curSongId, _curLyricsLoaded]);

  // YouTube IFrame API 로드 (마운트 시 1회)
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      initYTPlayer();
      return;
    }
    // 이미 스크립트 태그가 있으면 스킵
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const check = setInterval(() => {
        if (window.YT && window.YT.Player) {
          clearInterval(check);
          initYTPlayer();
        }
      }, 200);
      return () => clearInterval(check);
    }
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => initYTPlayer();

    return () => {
      window.onYouTubeIframeAPIReady = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function initYTPlayer() {
    if (ytPlayerRef.current) return;
    // 숨김 div 생성
    let container = document.getElementById('yt-player-hidden');
    if (!container) {
      container = document.createElement('div');
      container.id = 'yt-player-hidden';
      container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;';
      document.body.appendChild(container);
      const playerDiv = document.createElement('div');
      playerDiv.id = 'yt-player-element';
      container.appendChild(playerDiv);
    }

    ytPlayerRef.current = new window.YT.Player('yt-player-element', {
      height: '1',
      width: '1',
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        fs: 0,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
      },
      events: {
        onReady: () => {
          ytReadyRef.current = true;
        },
        onStateChange: (event) => {
          if (activeBackendRef.current !== 'youtube') return;
          const state = event.data;
          if (state === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
            setDuration(ytPlayerRef.current.getDuration() || 0);
            startYTTimer();
          } else if (state === window.YT.PlayerState.PAUSED) {
            setIsPlaying(false);
            stopYTTimer();
          } else if (state === window.YT.PlayerState.ENDED) {
            stopYTTimer();
            handleTrackEnded();
          } else if (state === window.YT.PlayerState.BUFFERING) {
            // 버퍼링 중
          }
        },
        onError: (event) => {
          if (activeBackendRef.current !== 'youtube') return;
          console.warn('[YT] 재생 오류 코드:', event.data);
          handleYTError(event.data);
        },
      },
    });
  }

  function startYTTimer() {
    stopYTTimer();
    ytTimerRef.current = setInterval(() => {
      if (ytPlayerRef.current && activeBackendRef.current === 'youtube' && !isDraggingRef.current) {
        const t = ytPlayerRef.current.getCurrentTime?.() || 0;
        setCurrentTime(t);
      }
    }, 500);
  }

  function stopYTTimer() {
    if (ytTimerRef.current) {
      clearInterval(ytTimerRef.current);
      ytTimerRef.current = null;
    }
  }

  // YouTube 오류 시 대체곡 검색 또는 스킵
  function handleYTError(errorCode) {
    const { playlist, currentIndex } = stateRef.current;
    const track = currentIndex !== null ? playlist[currentIndex] : null;
    if (!track?.playlistSongId) {
      // playlistSongId가 없으면 그냥 다음 곡으로 스킵
      handleTrackEnded();
      return;
    }
    // 서버에 대체곡 요청
    axios.post(`/public/playlists/0/replace-song`, {
      playlistSongId: track.playlistSongId,
      failedVideoId: track.youtubeVideoId,
    }).then((res) => {
      if (res.data?.youtubeVideoId) {
        // 플레이리스트 내 해당 트랙 업데이트
        setPlaylist((prev) => prev.map((t, i) =>
          i === currentIndex
            ? { ...t, youtubeVideoId: res.data.youtubeVideoId, nameKr: res.data.nameKr, albumImage: res.data.albumImage }
            : t
        ));
        // 대체곡 즉시 재생
        if (ytPlayerRef.current && ytReadyRef.current) {
          ytPlayerRef.current.loadVideoById(res.data.youtubeVideoId);
        }
      } else {
        handleTrackEnded();
      }
    }).catch(() => {
      handleTrackEnded();
    });
  }

  // 트랙 종료 시 다음 곡 결정 (audio onEnded + YT ENDED 공용)
  function handleTrackEnded() {
    const { repeatMode, currentIndex, playlist, shuffleMode, shuffleOrder } = stateRef.current;

    if (repeatMode === 'one') {
      // 한 곡 반복
      if (activeBackendRef.current === 'youtube' && ytPlayerRef.current) {
        ytPlayerRef.current.seekTo(0);
        ytPlayerRef.current.playVideo();
      } else {
        const audio = audioRef.current;
        audio.currentTime = 0;
        audio.play().catch(console.error);
      }
      return;
    }

    let nextIdx;
    if (shuffleMode && shuffleOrder.length > 0) {
      const pos = shuffleOrder.indexOf(currentIndex);
      if (pos >= shuffleOrder.length - 1) {
        if (repeatMode === 'all') nextIdx = shuffleOrder[0];
        else return;
      } else {
        nextIdx = shuffleOrder[pos + 1];
      }
    } else {
      const next = (currentIndex ?? -1) + 1;
      if (next >= playlist.length) {
        if (repeatMode === 'all') nextIdx = 0;
        else return;
      } else {
        nextIdx = next;
      }
    }

    if (nextIdx !== undefined && isPlayable(playlist[nextIdx])) {
      internalPlayTrack(nextIdx, playlist[nextIdx]);
    }
  }

  // 내부: 실제 트랙 재생 (백엔드 자동 분기)
  function internalPlayTrack(index, track) {
    setCurrentIndex(index);
    setCurrentTime(0);
    setDuration(0);

    if (track.youtubeVideoId) {
      // YouTube 백엔드
      audioRef.current.pause();
      activeBackendRef.current = 'youtube';
      if (ytPlayerRef.current && ytReadyRef.current) {
        ytPlayerRef.current.loadVideoById(track.youtubeVideoId);
      }
    } else if (track.fileUrl) {
      // HTML5 Audio 백엔드
      stopYTTimer();
      if (ytPlayerRef.current && ytReadyRef.current) {
        try { ytPlayerRef.current.pauseVideo(); } catch {}
      }
      activeBackendRef.current = 'audio';
      const audio = audioRef.current;
      audio.src = track.fileUrl;
      audio.play().catch(console.error);
    }
  }

  // 마운트 시: 복원된 플레이리스트의 현재 곡 src 세팅 (자동재생 없음)
  useEffect(() => {
    const { currentIndex: ci, playlist: pl } = stateRef.current;
    if (ci !== null && pl[ci]?.fileUrl) {
      audioRef.current.src = pl[ci].fileUrl;
    }
    // YouTube 트랙은 마운트 시 자동 로드하지 않음 (사용자 클릭 필요)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 오디오 이벤트 리스너 등록 (마운트 시 1회)
  useEffect(() => {
    const audio = audioRef.current;

    const onTimeUpdate = () => {
      if (!isDraggingRef.current && activeBackendRef.current === 'audio') {
        setCurrentTime(audio.currentTime);
      }
    };
    const onLoadedMetadata = () => {
      if (activeBackendRef.current === 'audio') setDuration(audio.duration || 0);
    };
    const onPlay = () => {
      if (activeBackendRef.current === 'audio') setIsPlaying(true);
    };
    const onPause = () => {
      if (activeBackendRef.current === 'audio') setIsPlaying(false);
    };
    const onEnded = () => {
      if (activeBackendRef.current !== 'audio') return;
      handleTrackEnded();
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    // Media Session API - 잠금화면/알림바 컨트롤 등록 (1회)
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => {
        if (activeBackendRef.current === 'youtube' && ytPlayerRef.current) {
          ytPlayerRef.current.playVideo();
        } else {
          audio.play().catch(console.error);
        }
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        if (activeBackendRef.current === 'youtube' && ytPlayerRef.current) {
          ytPlayerRef.current.pauseVideo();
        } else {
          audio.pause();
        }
      });
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        const ct = activeBackendRef.current === 'youtube'
          ? (ytPlayerRef.current?.getCurrentTime?.() || 0)
          : audio.currentTime;
        if (ct > 3) {
          if (activeBackendRef.current === 'youtube' && ytPlayerRef.current) {
            ytPlayerRef.current.seekTo(0);
          } else {
            audio.currentTime = 0;
          }
          setCurrentTime(0);
          return;
        }
        const { playlist, currentIndex, shuffleMode, shuffleOrder, repeatMode } = stateRef.current;
        let prevIdx;
        if (shuffleMode && shuffleOrder.length > 0) {
          const pos = shuffleOrder.indexOf(currentIndex);
          prevIdx = pos <= 0 ? shuffleOrder[shuffleOrder.length - 1] : shuffleOrder[pos - 1];
        } else {
          const prev = (currentIndex ?? 0) - 1;
          prevIdx = prev < 0 ? (repeatMode === 'all' ? playlist.length - 1 : 0) : prev;
        }
        if (isPlayable(playlist[prevIdx])) {
          internalPlayTrack(prevIdx, playlist[prevIdx]);
        }
      });
      navigator.mediaSession.setActionHandler('nexttrack', () => {
        const { playlist, currentIndex, shuffleMode, shuffleOrder, repeatMode } = stateRef.current;
        let nextIdx;
        if (shuffleMode && shuffleOrder.length > 0) {
          const pos = shuffleOrder.indexOf(currentIndex);
          nextIdx =
            pos >= shuffleOrder.length - 1
              ? repeatMode === 'all'
                ? shuffleOrder[0]
                : undefined
              : shuffleOrder[pos + 1];
        } else {
          const next = (currentIndex ?? -1) + 1;
          nextIdx =
            next >= playlist.length
              ? repeatMode === 'all'
                ? 0
                : undefined
              : next;
        }
        if (nextIdx !== undefined && isPlayable(playlist[nextIdx])) {
          internalPlayTrack(nextIdx, playlist[nextIdx]);
        }
      });
      navigator.mediaSession.setActionHandler('seekto', (details) => {
        if (details.seekTime !== undefined) {
          if (activeBackendRef.current === 'youtube' && ytPlayerRef.current) {
            ytPlayerRef.current.seekTo(details.seekTime, true);
          } else {
            audio.currentTime = details.seekTime;
          }
          setCurrentTime(details.seekTime);
        }
      });
    }

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.pause();
      stopYTTimer();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 볼륨/뮤트 동기화 (양쪽 백엔드 모두)
  useEffect(() => {
    const vol = isMuted ? 0 : volume;
    audioRef.current.volume = vol;
    if (ytPlayerRef.current && ytReadyRef.current) {
      ytPlayerRef.current.setVolume(vol * 100);
      if (isMuted) ytPlayerRef.current.mute();
      else ytPlayerRef.current.unMute();
    }
  }, [volume, isMuted]);

  // Media Session 메타데이터 업데이트 (잠금화면 곡정보)
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (currentIndex === null || !playlist[currentIndex]) {
      navigator.mediaSession.metadata = null;
      return;
    }
    const track = playlist[currentIndex];
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: track.nameKr || '',
      artist: track.artistKr || '',
      album: track.albumName || '',
      artwork: track.albumImage
        ? [{ src: track.albumImage, sizes: '512x512', type: 'image/jpeg' }]
        : [],
    });
  }, [currentIndex, playlist]);

  // pendingPlay 처리: playlist 변경 후 실제 재생
  useEffect(() => {
    if (!pendingPlayRef.current) return;
    const pending = pendingPlayRef.current;
    pendingPlayRef.current = null;

    const idx =
      pending._existingIdx !== undefined
        ? pending._existingIdx
        : playlist.findIndex((t) => t.id === pending.id);

    if (idx !== -1 && isPlayable(playlist[idx])) {
      internalPlayTrack(idx, playlist[idx]);
    }
  }, [playlist]); // eslint-disable-line react-hooks/exhaustive-deps

  const playTrack = useCallback((index) => {
    const { playlist } = stateRef.current;
    if (index === null || index < 0 || index >= playlist.length) return;
    const track = playlist[index];
    if (!isPlayable(track)) return;
    internalPlayTrack(index, track);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = useCallback(() => {
    const { currentIndex, playlist } = stateRef.current;

    // YouTube 백엔드가 활성 상태
    if (activeBackendRef.current === 'youtube' && ytPlayerRef.current && ytReadyRef.current) {
      const ytState = ytPlayerRef.current.getPlayerState();
      if (ytState === window.YT.PlayerState.PLAYING) {
        ytPlayerRef.current.pauseVideo();
      } else {
        ytPlayerRef.current.playVideo();
      }
      return;
    }

    // HTML5 Audio 백엔드
    const audio = audioRef.current;
    if (audio.paused) {
      if (currentIndex === null && playlist.length > 0) {
        playTrack(0);
      } else if (currentIndex !== null && !audio.src && isPlayable(playlist[currentIndex])) {
        playTrack(currentIndex);
      } else {
        audio.play().catch(console.error);
      }
    } else {
      audio.pause();
    }
  }, [playTrack]);

  const playNext = useCallback(() => {
    const { playlist, currentIndex, shuffleMode, shuffleOrder, repeatMode } = stateRef.current;
    let nextIdx;

    if (shuffleMode && shuffleOrder.length > 0) {
      const pos = shuffleOrder.indexOf(currentIndex);
      if (pos >= shuffleOrder.length - 1) {
        if (repeatMode === 'all') nextIdx = shuffleOrder[0];
        else return;
      } else {
        nextIdx = shuffleOrder[pos + 1];
      }
    } else {
      const next = (currentIndex ?? -1) + 1;
      if (next >= playlist.length) {
        if (repeatMode === 'all') nextIdx = 0;
        else return;
      } else {
        nextIdx = next;
      }
    }

    if (nextIdx !== undefined) playTrack(nextIdx);
  }, [playTrack]);

  const playPrev = useCallback(() => {
    const ct = activeBackendRef.current === 'youtube'
      ? (ytPlayerRef.current?.getCurrentTime?.() || 0)
      : audioRef.current.currentTime;
    if (ct > 3) {
      if (activeBackendRef.current === 'youtube' && ytPlayerRef.current) {
        ytPlayerRef.current.seekTo(0);
      } else {
        audioRef.current.currentTime = 0;
      }
      setCurrentTime(0);
      return;
    }

    const { playlist, currentIndex, shuffleMode, shuffleOrder, repeatMode } = stateRef.current;
    let prevIdx;

    if (shuffleMode && shuffleOrder.length > 0) {
      const pos = shuffleOrder.indexOf(currentIndex);
      prevIdx = pos <= 0 ? shuffleOrder[shuffleOrder.length - 1] : shuffleOrder[pos - 1];
    } else {
      const prev = (currentIndex ?? 0) - 1;
      if (prev < 0) {
        prevIdx = repeatMode === 'all' ? playlist.length - 1 : 0;
      } else {
        prevIdx = prev;
      }
    }

    if (prevIdx !== undefined) playTrack(prevIdx);
  }, [playTrack]);

  const seek = useCallback((time) => {
    if (activeBackendRef.current === 'youtube' && ytPlayerRef.current) {
      ytPlayerRef.current.seekTo(time, true);
    } else {
      audioRef.current.currentTime = time;
    }
    setCurrentTime(time);
  }, []);

  const setVolume = useCallback((vol) => {
    setVolumeState(vol);
    if (vol > 0) setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => setIsMuted((p) => !p), []);

  const toggleShuffle = useCallback(() => {
    setShuffleMode((prev) => {
      const next = !prev;
      if (next) {
        const { playlist, currentIndex } = stateRef.current;
        const others = Array.from({ length: playlist.length }, (_, i) => i).filter(
          (i) => i !== currentIndex
        );
        const shuffled = fisherYates(others);
        setShuffleOrder(currentIndex !== null ? [currentIndex, ...shuffled] : shuffled);
      }
      return next;
    });
  }, []);

  const cycleRepeat = useCallback(() => {
    setRepeatMode((prev) => {
      if (prev === 'none') return 'all';
      if (prev === 'all') return 'one';
      return 'none';
    });
  }, []);

  const showToast = useCallback((message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(message);
    toastTimerRef.current = setTimeout(() => setToast(null), 2500);
  }, []);

  const addToPlaylist = useCallback((song) => {
    setPlaylist((prev) => {
      if (prev.some((t) => t.id === song.id)) return prev;
      return [...prev, song];
    });
  }, []);

  // 여러 곡을 한 번에 추가하고 토스트 1회 표시 (맨 뒤에 추가)
  const addBulkToPlaylist = useCallback((songs) => {
    setPlaylist((prev) => {
      const toAdd = (songs || []).filter((s) => isPlayable(s) && !prev.some((p) => p.id === s.id));
      if (toAdd.length === 0) return prev;
      return [...prev, ...toAdd];
    });
    showToast('재생목록에 담김');
  }, [showToast]);

  const addAndPlay = useCallback((song) => {
    setPlaylist((prev) => {
      const existingIdx = prev.findIndex((t) => t.id === song.id);
      if (existingIdx !== -1) {
        pendingPlayRef.current = { ...song, _existingIdx: existingIdx };
        return prev;
      }
      pendingPlayRef.current = song;
      return [...prev, song];
    });
  }, []);

  // 클릭 핸들러에서 직접 호출 — 재생목록 맨 위에 삽입 + 즉시 재생
  // (브라우저 autoplay 정책 우회, 재생목록도 함께 업데이트)
  const playImmediate = useCallback((tracks) => {
    const valid = (tracks || []).filter((t) => isPlayable(t));
    if (valid.length === 0) return;

    const { playlist: curr, currentIndex } = stateRef.current;
    const first = valid[0];
    const existingIdx = curr.findIndex((t) => t.id === first.id);

    if (existingIdx !== -1) {
      // 이미 있는 곡 → 메타데이터 업데이트 + 맨 위로 이동
      const updated = curr.filter((_, i) => i !== existingIdx);
      const merged = { ...curr[existingIdx], ...first };
      // 나머지 새 곡도 맨 위에 추가
      const otherNew = valid.slice(1).filter((t) => !curr.some((p) => p.id === t.id));
      const newList = [merged, ...otherNew, ...updated];
      setPlaylist(newList);
      internalPlayTrack(0, merged);
    } else {
      // 새 곡 → 맨 위에 삽입
      const otherNew = valid.slice(1).filter((t) => !curr.some((p) => p.id === t.id));
      const newList = [first, ...otherNew, ...curr];
      setPlaylist(newList);
      internalPlayTrack(0, first);
    }
    setCurrentTime(0);
    setDuration(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFromPlaylist = useCallback((index) => {
    const { currentIndex } = stateRef.current;
    setPlaylist((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (currentIndex === index) {
        audioRef.current.pause();
        stopYTTimer();
        if (ytPlayerRef.current && ytReadyRef.current) {
          try { ytPlayerRef.current.pauseVideo(); } catch {}
        }
        activeBackendRef.current = 'audio';
        setIsPlaying(false);
        setCurrentIndex(null);
        setCurrentTime(0);
        setDuration(0);
      } else if (currentIndex !== null && currentIndex > index) {
        setCurrentIndex((ci) => ci - 1);
      }
      return next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearPlaylist = useCallback(() => {
    audioRef.current.pause();
    stopYTTimer();
    if (ytPlayerRef.current && ytReadyRef.current) {
      try { ytPlayerRef.current.pauseVideo(); } catch {}
    }
    activeBackendRef.current = 'audio';
    setPlaylist([]);
    setCurrentIndex(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSongList = useCallback(async (page = 1, search = '') => {
    setSongListLoading(true);
    try {
      const isPublicContext =
        window.location.hostname === 'impink.co.kr' ||
        window.location.hostname === 'www.impink.co.kr' ||
        window.location.hostname === 'urpink.co.kr' ||
        window.location.hostname === 'www.urpink.co.kr' ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '100.68.115.13' || window.location.hostname === '100.116.73.97' ||
        window.location.pathname.startsWith('/public');

      let res;
      if (isPublicContext) {
        res = await axios.get('/public/songs', { params: { page, limit: 20, search } });
      } else {
        // /song/audio 우선 시도, 서버 미배포 시 /song 으로 fallback
        try {
          res = await axios.get(`${process.env.REACT_APP_API_URL}/song/audio`, {
            params: { page, limit: 20, search },
          });
        } catch (audioErr) {
          if (audioErr.response?.status === 404) {
            res = await axios.get(`${process.env.REACT_APP_API_URL}/song`, {
              params: { page, limit: 20, search },
            });
          } else {
            throw audioErr;
          }
        }
      }
      const items = (res.data.items || []).filter((s) => s.fileUrl);
      setSongListItems(items);
      setSongListPage(res.data.currentPage || page);
      setSongListTotal(res.data.totalCount || 0);
      setSongListTotalPages(res.data.totalPages || 0);
    } catch (err) {
      console.error('곡 목록 불러오기 실패:', err);
    } finally {
      setSongListLoading(false);
    }
  }, []);

  const toggleSongList = useCallback(() => {
    setShowSongList((prev) => {
      const next = !prev;
      if (next) {
        setShowPlaylist(false);
        setSongListSearch('');
        setSongListPage(1);
        fetchSongList(1, '');
      }
      return next;
    });
  }, [fetchSongList]);

  const togglePlaylist = useCallback(() => {
    setShowPlaylist((prev) => {
      if (!prev) setShowSongList(false);
      return !prev;
    });
  }, []);

  const value = {
    playlist,
    currentIndex,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    shuffleMode,
    repeatMode,
    showSongList,
    showPlaylist,
    songListItems,
    songListPage,
    songListTotal,
    songListTotalPages,
    songListLoading,
    songListSearch,
    setSongListSearch,
    isDraggingRef,
    toast,
    lyricsBarVisible,
    setLyricsBarVisible,
    showToast,
    addToPlaylist,
    addBulkToPlaylist,
    addAndPlay,
    playImmediate,
    removeFromPlaylist,
    clearPlaylist,
    playTrack,
    togglePlay,
    playNext,
    playPrev,
    seek,
    setVolume,
    toggleMute,
    toggleShuffle,
    cycleRepeat,
    fetchSongList,
    toggleSongList,
    togglePlaylist,
  };

  return <MusicPlayerContext.Provider value={value}>{children}</MusicPlayerContext.Provider>;
}

export const useMusicPlayer = () => {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  return ctx;
};
